import { useEffect, useState } from "react";

import { useAuth } from "contexts/authContext";
import { useManualOccurrence } from "contexts/register/ManualOccurrence";
import { socket } from "hooks/useSocket";
import { IPoliceman } from "interfaces/Policeman";
import OccurrenceManagementService from "services/OccurrenceManagementService";
import { fireToast } from "services/sweetAlertConfig";
import { treatSocketLocations } from "utils/treatSocketLocations";
import { useDataList } from "hooks/useDataList";

export type Coords = {
  latitude: number;
  longitude: number;
};

export type MarkerType =
  | "policeman"
  | "occVehicle"
  | "occPatrimony"
  | "store"
  | "policemanOnDuty";

export type Markers = {
  id: string;
  coords: Coords;
  type: MarkerType;
  name?: string;
  working?: boolean;
};

export type SocketMarkers = {
  [id: string]: SocketMarkerBody;
};

export type SocketMarkerBody = {
  coords: Coords;
  token: string;
  working: boolean;
  name?: string;
};

export type RegisterPoliceman = {
  name: string;
  phoneNumber: string;
};

export const defaultCoords = {
  lat: -23.556831106,
  lng: -46.653830718,
}

export const defaultRegisterPoliceman = { name: "", phoneNumber: "" }

export function usePolicemanTab() {
  const [socketPolicemen, setSocketPolicemen] = useState<Markers[]>([]);
  const [registerPoliceman, setRegisterPoliceman] = useState<boolean>(false);
  const [associationSending, setAssociationSending] = useState<boolean>(false);
  const [superficialPolicemanArray, setSuperficialPolicemanArray] = useState<Array<RegisterPoliceman>>([])
  const [policemanArray, setPolicemanArray] = useState<Array<IPoliceman>>([])
  const [onlinePolicemen, setOnlinePolicemen] = useState<IPoliceman[]>([]);
  const [registerPolicemanData, setRegisterPolicemanData] = useState<RegisterPoliceman>(defaultRegisterPoliceman);
  const [mapCoords, setMapCoords] = useState<google.maps.LatLngLiteral>(defaultCoords);
  const [selectedPoliceman, setSelectedPoliceman] = useState<IPoliceman | null>(null);

  const { occurrenceToEdit, refetch } = useManualOccurrence();
  const { tenant } = useAuth();

  const policemenData = useDataList<IPoliceman>({
    queryKey: 'listAllPolicemen',
    route: 'policemen',
    filter: {
      relations: { user: true },
      notPage: true,
    }
  }).data;

  useEffect(() => {
    if (tenant?.id) {
      socket.emit("listLocations", tenant.id);
      socket.on("listLocations", onLocationChange);
    }

    return () => {
      socket.off("listLocations");
    };
  }, [policemenData, tenant]);

  useEffect(() => {
    if (!selectedPoliceman) return;

    const selectedSocketPolicemanCoords = socketPolicemen.find(
      (policeman) => policeman.id === selectedPoliceman.id
    )?.coords;

    if (!selectedSocketPolicemanCoords) return;

    setMapCoords({
      lat: selectedSocketPolicemanCoords.latitude,
      lng: selectedSocketPolicemanCoords.longitude,
    });
  }, [selectedPoliceman, socketPolicemen]);

  useEffect(() => {
    if (!selectedPoliceman) return;

    const selectedSocketPolicemanCoords = socketPolicemen.find(
      (policeman) => policeman.id === selectedPoliceman.id
    )?.coords;

    if (!selectedSocketPolicemanCoords) return;

    setMapCoords({
      lat: selectedSocketPolicemanCoords.latitude,
      lng: selectedSocketPolicemanCoords.longitude,
    });
  }, [selectedPoliceman, socketPolicemen]);

  useEffect(() => {
    const online: IPoliceman[] = [];

    for (let marker of socketPolicemen) {
      if (policemenData && policemenData.items)
        for (let policeman of policemenData.items) {
          if (policeman.id === marker.id && !marker.working) {
            online.push(policeman);
          }
        }
    }

    setOnlinePolicemen(online);
  }, [socketPolicemen, registerPolicemanData]);

  useEffect(() => {
    setRegisterPolicemanData(defaultRegisterPoliceman)
  }, [registerPoliceman])

  async function handleAssociatePoliceman() {
    setAssociationSending(true)
    const isSomePolicemanSelected = policemanArray.length > 0 || superficialPolicemanArray.length > 0

    if (!isSomePolicemanSelected) {
      setAssociationSending(false)
      return
    };

    const selectedRegisteredPolicemanIds = policemanArray.map((policeman) => policeman.id)

    try {
      await OccurrenceManagementService.acceptOccurrence({
        occurrenceId: occurrenceToEdit.id,
        type: occurrenceToEdit.patrimonialType,
        policemenId: selectedRegisteredPolicemanIds,
        superficialPolicemen: superficialPolicemanArray
      });

      fireToast("Pronta resposta associado com sucesso!");
      refetch();
    } catch {
      fireToast(
        "Ocorreu um erro ao associar esse pronta-resposta."
      );
    } finally {
      setAssociationSending(false)
    }
  }

  function onLocationChange(locations: SocketMarkers) {
    const _locations = treatSocketLocations({
      socketLocations: locations,
      tenantId: tenant?.id ?? "",
    });

    const onlinePolicemen = [];

    for (let policeman of _locations) {
      if (!policeman.working) {
        onlinePolicemen.push(policeman);
      }
    }

    setSocketPolicemen([...onlinePolicemen]);
  }

  function handleAddPoliceman() {
    if (registerPolicemanData.name !== '' && !selectedPoliceman) {
      setSuperficialPolicemanArray(prev => [...prev, registerPolicemanData])
    }

    if (registerPolicemanData.name === '' && selectedPoliceman) {
      setPolicemanArray(prev => [...prev, selectedPoliceman])
    }

    // reset single states
    setSelectedPoliceman(null)
    setRegisterPolicemanData(defaultRegisterPoliceman)
  }

  function removePolicemanByName(name: string, isRegistered: boolean) {
    if (isRegistered) {
      setPolicemanArray(prev => prev.filter(policeman => policeman.user.name !== name))
    } else {
      setSuperficialPolicemanArray(prev => prev.filter(superficialPoliceman => superficialPoliceman.name !== name))
    }
  }

  return {
    registerPoliceman,
    onlinePolicemen,
    selectedPoliceman,
    mapCoords,
    policemanArray,
    superficialPolicemanArray,
    registerPolicemanData,
    removePolicemanByName,
    setSuperficialPolicemanArray,
    setRegisterPoliceman,
    handleAddPoliceman,
    setSelectedPoliceman,
    setRegisterPolicemanData,
    handleAssociatePoliceman,
    setPolicemanArray,
    associationSending, 
    setAssociationSending
  }
}